import React from 'react';
import { t } from 'i18next';
import { Input, Button, TrashIcon } from '@rabbit/elements/shared-components';
import { toast } from 'react-toastify';
import { FormikProps } from 'formik';
import { AddEditVendableFormValuesShape } from '@rabbit/elements/shared-types';
import { SageFileUploader } from '../../upload-wrapper/SageFileUploader';
import {
  DocTypeShapeTypes,
  FileUploaderVariations,
  UploadedFileCategories,
} from '@rabbit/elements/shared-types';
import {
  PersonaIdTypeSplitter,
  PersonaTypeSingleLetter,
} from '@rabbit/data/types';
import { PlusIcon } from '@heroicons/react/24/solid';

interface SerialNumberReferenceProps {
  props: FormikProps<AddEditVendableFormValuesShape>;
  vendableId?: string;
  newVendableId?: string;
  activePremiumTenant: string;
  parsedSerialNumbers: string[];
}

export const SerialNumberReference: React.FC<SerialNumberReferenceProps> = ({
  props,
  vendableId,
  newVendableId,
  activePremiumTenant,
  parsedSerialNumbers,
}) => {
  // Function to compare alphanumeric strings
  const compareAlphanumeric = (a: string, b: string) => {
    // Natural sort comparison for alphanumeric strings
    return a.localeCompare(b, undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  };

  return (
    <div className="mt-4 rounded-lg border border-gray-200 p-4">
      <h3 className="font-nunito mb-4 text-lg">{t('general.serialNumbers')}</h3>

      {/* Individual Serial Numbers */}
      <div className="mb-4">
        <h4 className="font-nunito mb-2">
          {t('general.addIndividualSerialNumbers')}
        </h4>
        <div className="mb-2 flex gap-2">
          <Input
            type="text"
            name="newSerialNumber"
            settings={{
              placeholder: t('general.enterSerialNumber'),
              value: props.values.newSerialNumber || '',
            }}
            onChange={(e) => {
              props.setFieldValue('newSerialNumber', e.target.value);
            }}
          />
          <Button
            kind="primary"
            type="button"
            icon={<PlusIcon />}
            children={t('general.add')}
            onClick={() => {
              if (props.values.newSerialNumber) {
                // Ensure serialNumbers is an array
                const currentSerialNumbers = props.values.serialNumbers || [];
                props.setFieldValue('serialNumbers', [
                  ...currentSerialNumbers,
                  props.values.newSerialNumber,
                ]);
                props.setFieldValue('newSerialNumber', '');
              }
            }}
          />
        </div>

        {/* Display added serial numbers */}
        {props.values.serialNumbers &&
          props.values.serialNumbers.length > 0 && (
            <div className="mt-2">
              <div className="font-nunito mb-1 text-sm">
                {t('general.addedSerialNumbers')}:
              </div>
              <div className="flex flex-wrap gap-2">
                {props.values.serialNumbers.map((serialNumber, index) => (
                  <div
                    key={index}
                    className="flex items-center rounded bg-gray-100 px-2 py-1"
                  >
                    <span className="mr-2">{serialNumber}</span>
                    <button
                      type="button"
                      className="text-red-500"
                      onClick={() => {
                        // Ensure serialNumbers is an array
                        const currentSerialNumbers =
                          props.values.serialNumbers || [];
                        const newSerialNumbers = [...currentSerialNumbers];
                        newSerialNumbers.splice(index, 1);
                        props.setFieldValue('serialNumbers', newSerialNumbers);
                      }}
                    >
                      <TrashIcon onClick={() => {}} className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
      </div>

      {/* Serial Number Ranges */}
      <div className="mb-4">
        <h4 className="font-nunito mb-2">
          {t('general.addSerialNumberRange')}
        </h4>
        <div className="mb-2 flex gap-2">
          <Input
            type="text"
            name="newRangeStart"
            settings={{
              placeholder: t('general.startSerialNumber'),
              value: props.values.newRangeStart || '',
            }}
            onChange={(e) => {
              props.setFieldValue('newRangeStart', e.target.value);
            }}
          />

          <Input
            type="text"
            name="newRangeEnd"
            settings={{
              placeholder: t('general.endSerialNumber'),
              value: props.values.newRangeEnd || '',
            }}
            onChange={(e) => {
              props.setFieldValue('newRangeEnd', e.target.value);
            }}
          />
          <Button
            kind={'primary'}
            type="button"
            icon={<PlusIcon />}
            children={t('general.addRange')}
            onClick={() => {
              if (props.values.newRangeStart && props.values.newRangeEnd) {
                // Validate that start is less than or equal to end
                const start = props.values.newRangeStart;
                const end = props.values.newRangeEnd;

                // Check if the range is valid using alphanumeric comparison
                if (compareAlphanumeric(start, end) > 0) {
                  toast.error(t('message.startSerialNumberMustBeLessThanEnd'));
                  return;
                }

                // Ensure serialNumberRanges is an array
                const currentRanges = props.values.serialNumberRanges || [];

                // Add Symbol.iterator method if it doesn't exist
                if (!currentRanges[Symbol.iterator]) {
                  Object.defineProperty(currentRanges, Symbol.iterator, {
                    enumerable: false,
                    configurable: true,
                    writable: true,
                    value: Array.prototype[Symbol.iterator],
                  });
                }

                props.setFieldValue('serialNumberRanges', [
                  ...currentRanges,
                  {
                    start: props.values.newRangeStart,
                    end: props.values.newRangeEnd,
                  },
                ]);
                props.setFieldValue('newRangeStart', '');
                props.setFieldValue('newRangeEnd', '');
              } else {
                // Show error if either start or end is missing
                if (!props.values.newRangeStart && !props.values.newRangeEnd) {
                  toast.error(
                    t('message.pleaseEnterBothStartAndEndSerialNumbers')
                  );
                } else if (!props.values.newRangeStart) {
                  toast.error(t('message.pleaseEnterStartSerialNumber'));
                } else if (!props.values.newRangeEnd) {
                  toast.error(t('message.pleaseEnterEndSerialNumber'));
                }
              }
            }}
          />
        </div>

        {/* Display added ranges */}
        {props.values.serialNumberRanges &&
          props.values.serialNumberRanges.length > 0 && (
            <div className="mt-2">
              <div className="font-nunito mb-1 text-sm">
                {t('general.addedSerialNumberRanges')}:
              </div>
              <div className="flex flex-wrap gap-2">
                {props.values.serialNumberRanges.map((range, index) => (
                  <div
                    key={index}
                    className="flex items-center rounded bg-gray-100 px-2 py-1"
                  >
                    <span className="mr-2">
                      {range.start} - {range.end}
                    </span>
                    <button
                      type="button"
                      className="text-red-500"
                      onClick={() => {
                        // Ensure serialNumberRanges is an array
                        const currentRanges =
                          props.values.serialNumberRanges ?? [];

                        // Add Symbol.iterator method if it doesn't exist
                        if (!currentRanges[Symbol.iterator]) {
                          Object.defineProperty(
                            currentRanges,
                            Symbol.iterator,
                            {
                              enumerable: false,
                              configurable: true,
                              writable: true,
                              value: Array.prototype[Symbol.iterator],
                            }
                          );
                        }

                        const newRanges = currentRanges.filter(
                          (_, rangeIndex) => rangeIndex !== index
                        );

                        props.setFieldValue('serialNumberRanges', newRanges);
                      }}
                    >
                      <TrashIcon onClick={() => {}} className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default SerialNumberReference;